import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Loader2, Target, CheckCircle2, Circle, Save, X, Edit3, Calendar, User } from 'lucide-react';
import { toast } from 'sonner';

const STATUS = [
  { v: 'planning', label: 'Planning', color: 'var(--text-low)' },
  { v: 'active', label: 'Active', color: 'var(--accent)' },
  { v: 'on_hold', label: 'On hold', color: 'var(--warn)' },
  { v: 'done', label: 'Done', color: 'var(--good)' },
  { v: 'cancelled', label: 'Cancelled', color: 'var(--danger)' },
];

const COLORS = ['#B49BFF', '#FFD66B', '#6BE0A1', '#FFB05A', '#FF7A85', '#5DADE2', '#F48FB1'];

export default function ProjectsPanel({ api }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(empty());
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/ceo-life/projects');
      setProjects(data || []);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = statusFilter === 'all' ? projects : projects.filter(p => p.status === statusFilter);

  const open = (p) => { setActive(p); setEditing(false); setCreating(false); setDraft({ ...p }); };
  const startCreate = () => { setActive(null); setCreating(true); setEditing(true); setDraft(empty()); };

  const save = async () => {
    if (!draft.name.trim()) { toast.error('Name required'); return; }
    try {
      if (draft.id) {
        const { data } = await api.put(`/ceo-life/projects/${draft.id}`, draft);
        setProjects(prev => prev.map(p => p.id === data.id ? data : p));
        setActive(data);
        toast.success('Updated');
      } else {
        const { data } = await api.post('/ceo-life/projects', draft);
        setProjects(prev => [data, ...prev]);
        setActive(data);
        setCreating(false);
        toast.success('Project created');
      }
      setEditing(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await api.delete(`/ceo-life/projects/${id}`);
      setProjects(prev => prev.filter(p => p.id !== id));
      if (active?.id === id) { setActive(null); setEditing(false); }
    } catch { toast.error('Failed'); }
  };

  const toggleMilestone = async (idx) => {
    const next = (active.milestones || []).map((m, i) => i === idx ? { ...m, done: !m.done } : m);
    try {
      const { data } = await api.put(`/ceo-life/projects/${active.id}`, { milestones: next });
      setActive(data); setDraft(data);
      setProjects(prev => prev.map(p => p.id === data.id ? data : p));
    } catch { toast.error('Failed'); }
  };

  const toggleTask = async (projectId, taskId) => {
    try {
      const { data } = await api.post(`/ceo-life/projects/${projectId}/tasks/${taskId}/toggle`);
      setProjects(prev => prev.map(p => p.id === data.id ? data : p));
      if (active?.id === data.id) { setActive(data); setDraft(data); }
    } catch { toast.error('Failed'); }
  };

  const addNote = async (projectId, text) => {
    try {
      await api.post(`/ceo-life/projects/${projectId}/notes`, { text });
      const { data } = await api.get('/ceo-life/projects');
      setProjects(data || []);
      if (active) setActive(data.find(p => p.id === projectId) || active);
      toast.success('Note added');
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>;

  return (
    <div className="space-y-4" data-testid="ceo-projects">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={()=>setStatusFilter('all')} className={`ceo-tab-btn ${statusFilter==='all'?'active':''}`} style={{ fontSize: 12, padding:'6px 12px' }}>All ({projects.length})</button>
        {STATUS.map(s => {
          const count = projects.filter(p=>p.status===s.v).length;
          if (count === 0 && s.v !== 'active') return null;
          return <button key={s.v} onClick={()=>setStatusFilter(s.v)} className={`ceo-tab-btn ${statusFilter===s.v?'active':''}`} style={{ fontSize: 12, padding:'6px 12px' }}>
            <span className="ceo-priority-dot" style={{ background: s.color, boxShadow:'none' }} />{s.label} ({count})
          </button>;
        })}
        <button onClick={startCreate} className="ceo-btn ceo-btn-primary ml-auto" data-testid="ceo-proj-new"><Plus className="h-3.5 w-3.5" />New project</button>
      </div>

      {(creating || editing) && active && (
        <ProjectForm draft={draft} setDraft={setDraft} onCancel={() => { setEditing(false); setCreating(false); if (active && !creating) setDraft({ ...active }); else setActive(null); }} onSave={save} />
      )}
      {creating && !active && (
        <ProjectForm draft={draft} setDraft={setDraft} onCancel={() => { setCreating(false); setEditing(false); }} onSave={save} />
      )}

      {filtered.length === 0 && !creating ? (
        <div className="ceo-empty"><Target className="ceo-empty-icon h-9 w-9" /><p className="text-sm">No projects in this view. Click <strong>New project</strong> to start tracking an initiative.</p></div>
      ) : !editing ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {filtered.map(p => (
            <ProjectCard
              key={p.id}
              p={p}
              isOpen={active?.id === p.id}
              onClick={() => open(p)}
              onEdit={() => { setActive(p); setDraft({ ...p }); setEditing(true); setCreating(false); }}
              onDelete={() => remove(p.id)}
              onToggleMilestone={(i) => { setActive(p); toggleMilestone(i); }}
              onToggleTask={(tid) => toggleTask(p.id, tid)}
              onAddNote={(text) => addNote(p.id, text)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ProjectCard({ p, isOpen, onClick, onEdit, onDelete, onToggleMilestone, onToggleTask, onAddNote }) {
  const status = STATUS.find(s => s.v === p.status) || STATUS[0];
  const ms = p.milestones || [];
  const tasks = p.tasks || [];
  const notes = p.notes || [];
  const budget = p.budget || 0;
  const spent = p.budget_spent || 0;
  const [noteDraft, setNoteDraft] = React.useState('');
  const budgetStatusColor = p.budget_status_computed === 'over' ? 'var(--danger)' : p.budget_status_computed === 'watch' ? 'var(--warn)' : 'var(--good)';
  return (
    <div className="ceo-card-elevated p-4 transition-all" data-testid={`ceo-project-${p.id}`} style={{ borderLeft: `3px solid ${p.color || 'var(--accent)'}` }}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <button onClick={onClick} className="text-left flex-1 min-w-0">
          <h3 className="text-base font-bold" style={{ color: 'var(--text)' }}>{p.name}</h3>
          {p.owner && <p className="text-xs flex items-center gap-1 mt-0.5" style={{ color: 'var(--text-dim)' }}><User className="h-2.5 w-2.5" />{p.owner}</p>}
        </button>
        <div className="flex items-center gap-1">
          <button onClick={onEdit} className="ceo-btn ceo-btn-ghost" style={{ padding: '4px 6px' }}><Edit3 className="h-3 w-3" /></button>
          <button onClick={onDelete} className="ceo-btn ceo-btn-danger" style={{ padding: '4px 6px' }}><Trash2 className="h-3 w-3" /></button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="ceo-pill" style={{ color: status.color, fontSize: 10 }}><span className="ceo-priority-dot" style={{ background: status.color, boxShadow:'none' }} />{status.label}</span>
        {p.target_date && <span className="ceo-pill" style={{ fontSize: 10 }}><Calendar className="h-2.5 w-2.5" />{formatDate(p.target_date)}</span>}
        {budget > 0 && (
          <span className="ceo-pill" style={{ fontSize: 10, color: budgetStatusColor }}>
            ${spent.toLocaleString()} / ${budget.toLocaleString()} · {p.budget_status_computed}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="ceo-eyebrow">Progress {p.auto_progress === false ? '· manual' : ''}</span>
          <span className="text-xs font-bold" style={{ color: 'var(--text)' }}>{p.percent_done || 0}%</span>
        </div>
        <div style={{ height: 6, borderRadius: 3, background: 'var(--ink-3)', overflow: 'hidden' }}>
          <div style={{ width: `${p.percent_done || 0}%`, height: '100%', background: `linear-gradient(90deg, ${p.color || 'var(--accent)'}, var(--accent))`, transition: 'width 300ms ease' }} />
        </div>
      </div>

      {p.description && (
        <p className="text-xs mb-3 line-clamp-2" style={{ color: 'var(--text-dim)' }}>{p.description}</p>
      )}

      {tasks.length > 0 && (
        <div className="mb-3">
          <div className="ceo-eyebrow mb-1.5">Tasks · {tasks.filter(t=>t.done).length}/{tasks.length}</div>
          <div className="space-y-1">
            {tasks.slice(0, isOpen ? 100 : 4).map((t, i) => (
              <button key={t.id||i} onClick={(e) => { e.stopPropagation(); onToggleTask(t.id); }} className="w-full flex items-start gap-2 text-left text-xs hover:bg-[var(--ink-3)] rounded px-1.5 py-1" data-testid={`ceo-task-${p.id}-${t.id}`}>
                {t.done ? <CheckCircle2 className="h-3 w-3 shrink-0 mt-0.5" style={{ color: 'var(--good)' }} /> : <Circle className="h-3 w-3 shrink-0 mt-0.5" style={{ color: 'var(--text-low)' }} />}
                <div className="flex-1 min-w-0">
                  <div className={t.done?'line-through':''} style={{ color: t.done ? 'var(--text-low)' : 'var(--text)' }}>{t.title}</div>
                  {(t.owner || t.due_date) && <div className="text-[10px]" style={{ color: 'var(--text-low)' }}>{t.owner}{t.owner && t.due_date ? ' · ' : ''}{t.due_date ? formatDate(t.due_date) : ''}</div>}
                </div>
              </button>
            ))}
            {!isOpen && tasks.length > 4 && <button onClick={onClick} className="text-[11px]" style={{ color: 'var(--accent)' }}>+ {tasks.length-4} more</button>}
          </div>
        </div>
      )}

      {ms.length > 0 && (
        <div className="mb-3">
          <div className="ceo-eyebrow mb-1.5">Milestones · {ms.filter(m=>m.done).length}/{ms.length}</div>
          <div className="space-y-1">
            {ms.slice(0, isOpen ? 100 : 3).map((m, i) => (
              <button key={m.id||i} onClick={(e) => { e.stopPropagation(); onToggleMilestone(i); }} className="w-full flex items-center gap-2 text-left text-xs hover:bg-[var(--ink-3)] rounded px-1.5 py-1" data-testid={`ceo-milestone-${p.id}-${i}`}>
                {m.done ? <CheckCircle2 className="h-3 w-3 shrink-0" style={{ color: 'var(--good)' }} /> : <Circle className="h-3 w-3 shrink-0" style={{ color: 'var(--text-low)' }} />}
                <span className={m.done?'line-through':''} style={{ color: m.done ? 'var(--text-low)' : 'var(--text)' }}>{m.title}</span>
                {m.due_date && <span className="ml-auto text-[10px]" style={{ color: 'var(--text-low)' }}>{formatDate(m.due_date)}</span>}
              </button>
            ))}
            {!isOpen && ms.length > 3 && <button onClick={onClick} className="text-[11px]" style={{ color: 'var(--accent)' }}>+ {ms.length-3} more</button>}
          </div>
        </div>
      )}

      {isOpen && (
        <div>
          <div className="ceo-eyebrow mb-1.5">Notes & updates</div>
          <div className="flex gap-1 mb-2">
            <input
              className="ceo-input flex-1"
              placeholder="Quick update… (e.g., Got quote from vendor)"
              value={noteDraft}
              onChange={e => setNoteDraft(e.target.value)}
              onClick={e => e.stopPropagation()}
              data-testid={`ceo-project-note-input-${p.id}`}
            />
            <button
              className="ceo-btn ceo-btn-primary"
              disabled={!noteDraft.trim()}
              onClick={(e) => { e.stopPropagation(); onAddNote(noteDraft); setNoteDraft(''); }}
              data-testid={`ceo-project-note-add-${p.id}`}
            ><Plus className="h-3 w-3" /></button>
          </div>
          {notes.length === 0 ? (
            <p className="text-[11px] italic" style={{ color: 'var(--text-low)' }}>No updates logged yet.</p>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {notes.map(n => (
                <div key={n.id} className="text-xs px-2 py-1.5 rounded" style={{ background: 'var(--ink-3)' }}>
                  <p style={{ color: 'var(--text)' }}>{n.text}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-low)' }}>{n.author || 'Unknown'} · {formatDate(n.created_at?.slice(0,10))}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProjectForm({ draft, setDraft, onCancel, onSave }) {
  const set = (k, v) => setDraft(p => ({ ...p, [k]: v }));
  const setMS = (i, k, v) => set('milestones', (draft.milestones||[]).map((m, idx) => idx===i?{...m,[k]:v}:m));
  const addMS = () => set('milestones', [...(draft.milestones||[]), { id:`ms-${Date.now()}`, title:'', done:false }]);
  const removeMS = (i) => set('milestones', (draft.milestones||[]).filter((_,idx)=>idx!==i));
  const setTask = (i, k, v) => set('tasks', (draft.tasks||[]).map((t, idx) => idx===i?{...t,[k]:v}:t));
  const addTask = () => set('tasks', [...(draft.tasks||[]), { id:`t-${Date.now()}`, title:'', owner:'', due_date:'', priority:'normal', done:false }]);
  const removeTask = (i) => set('tasks', (draft.tasks||[]).filter((_,idx)=>idx!==i));

  return (
    <div className="ceo-card-elevated p-5 space-y-3" data-testid="ceo-proj-form">
      <h3 className="ceo-headline text-xl">{draft.id ? 'Edit project' : 'New project'}</h3>
      <input className="ceo-input" placeholder="Project name" autoFocus value={draft.name||''} onChange={e=>set('name',e.target.value)} data-testid="ceo-proj-name" />
      <textarea rows={2} className="ceo-textarea" placeholder="What's this project about?" value={draft.description||''} onChange={e=>set('description',e.target.value)} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div><label className="ceo-eyebrow">Owner</label><input className="ceo-input" value={draft.owner||''} onChange={e=>set('owner',e.target.value)} /></div>
        <div><label className="ceo-eyebrow">Status</label><select className="ceo-select" value={draft.status||'planning'} onChange={e=>set('status',e.target.value)}>{STATUS.map(s=><option key={s.v} value={s.v}>{s.label}</option>)}</select></div>
        <div><label className="ceo-eyebrow">Target date</label><input type="date" className="ceo-input" value={draft.target_date||''} onChange={e=>set('target_date',e.target.value)} /></div>
        <div><label className="ceo-eyebrow">Priority</label><select className="ceo-select" value={draft.priority||'normal'} onChange={e=>set('priority',e.target.value)}>{['low','normal','high','critical'].map(p=><option key={p} value={p}>{p}</option>)}</select></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div><label className="ceo-eyebrow">Budget ($)</label><input type="number" step="0.01" className="ceo-input" value={draft.budget||''} onChange={e=>set('budget',parseFloat(e.target.value)||0)} data-testid="ceo-proj-budget" /></div>
        <div><label className="ceo-eyebrow">Spent ($)</label><input type="number" step="0.01" className="ceo-input" value={draft.budget_spent||''} onChange={e=>set('budget_spent',parseFloat(e.target.value)||0)} data-testid="ceo-proj-spent" /></div>
        <div className="col-span-2 flex items-end gap-3">
          <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-dim)' }}>
            <input type="checkbox" className="ceo-checkbox" checked={draft.auto_progress !== false} onChange={e=>set('auto_progress', e.target.checked)} />
            Auto-compute progress from tasks+milestones
          </label>
          {draft.auto_progress === false && (
            <input type="number" min="0" max="100" className="ceo-input" placeholder="%" style={{ width: 80 }} value={draft.percent_override ?? ''} onChange={e=>set('percent_override', parseInt(e.target.value)||0)} />
          )}
        </div>
      </div>
      <div>
        <label className="ceo-eyebrow">Color</label>
        <div className="flex gap-1.5 mt-1">
          {COLORS.map(c => (
            <button key={c} type="button" onClick={()=>set('color',c)} style={{ width: 28, height: 28, borderRadius: 6, background: c, border: draft.color === c ? '2px solid white' : '2px solid var(--line)' }} />
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="ceo-eyebrow">Tasks</label>
          <button type="button" onClick={addTask} className="ceo-btn ceo-btn-ghost" style={{ padding:'3px 8px', fontSize:10 }} data-testid="ceo-proj-add-task"><Plus className="h-2.5 w-2.5" />Add task</button>
        </div>
        {(draft.tasks||[]).map((t, i) => (
          <div key={t.id||i} className="grid grid-cols-12 gap-1.5 mb-1.5 items-center">
            <input type="checkbox" className="ceo-checkbox col-span-1" checked={!!t.done} onChange={e=>setTask(i,'done',e.target.checked)} />
            <input className="ceo-input col-span-5" placeholder="Task" value={t.title||''} onChange={e=>setTask(i,'title',e.target.value)} data-testid={`ceo-proj-task-title-${i}`} />
            <input className="ceo-input col-span-2" placeholder="Owner" value={t.owner||''} onChange={e=>setTask(i,'owner',e.target.value)} />
            <input type="date" className="ceo-input col-span-2" value={t.due_date||''} onChange={e=>setTask(i,'due_date',e.target.value)} />
            <select className="ceo-select col-span-1" value={t.priority||'normal'} onChange={e=>setTask(i,'priority',e.target.value)}>{['low','normal','high','critical'].map(p=><option key={p} value={p}>{p[0].toUpperCase()}</option>)}</select>
            <button type="button" onClick={()=>removeTask(i)} className="ceo-btn ceo-btn-danger col-span-1" style={{ padding: '0 6px' }}><X className="h-3 w-3" /></button>
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="ceo-eyebrow">Milestones</label>
          <button type="button" onClick={addMS} className="ceo-btn ceo-btn-ghost" style={{ padding:'3px 8px', fontSize:10 }}><Plus className="h-2.5 w-2.5" />Add</button>
        </div>
        {(draft.milestones||[]).map((m, i) => (
          <div key={m.id||i} className="flex gap-2 mb-1.5 items-center">
            <input type="checkbox" className="ceo-checkbox" checked={!!m.done} onChange={e=>setMS(i,'done',e.target.checked)} />
            <input className="ceo-input flex-1" placeholder="Milestone" value={m.title||''} onChange={e=>setMS(i,'title',e.target.value)} />
            <input type="date" className="ceo-input" style={{ width: 150 }} value={m.due_date||''} onChange={e=>setMS(i,'due_date',e.target.value)} />
            <button type="button" onClick={()=>removeMS(i)} className="ceo-btn ceo-btn-danger" style={{ padding: '0 8px' }}><X className="h-3 w-3" /></button>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="ceo-btn ceo-btn-ghost"><X className="h-3 w-3" />Cancel</button>
        <button onClick={onSave} className="ceo-btn ceo-btn-primary" data-testid="ceo-proj-save"><Save className="h-3 w-3" />{draft.id?'Update':'Create project'}</button>
      </div>
    </div>
  );
}

function empty() { return { name:'', description:'', owner:'', status:'planning', color:'#B49BFF', target_date:'', priority:'normal', milestones:[], tasks:[], notes:[], budget:0, budget_spent:0, auto_progress:true, percent_override:null }; }
function formatDate(d) { if (!d) return ''; try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric' }); } catch { return d; } }
